import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { BUDGET_CATEGORIES, type Project } from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { useToast } from '@/features/ui/toast/useToast';
import './budget.css';
import { Link } from 'react-router-dom';

type Line = {
  id: string;
  category: string;
  label: string;
  amount: number;
  kind: string;
  notes: string;
  line_date: string;
};

export function BudgetPage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    lineId: string | null;
    lineLabel: string;
  }>({ open: false, lineId: null, lineLabel: '' });
  const [deletingLine, setDeletingLine] = useState(false);
  const todayIso = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    category: BUDGET_CATEGORIES[0] as string,
    label: '',
    amount: 0,
    kind: 'expense',
    notes: '',
    line_date: todayIso,
  });

  useEffect(() => {
    if (!profile) return;
    void supabase
      .from('projects')
      .select('*')
      .eq('user_id', profile.id)
      .then(({ data }) => {
        const list = (data ?? []) as Project[];
        setProjects(list);
        if (list[0]) setProjectId(list[0].id);
      });
  }, [profile]);

  async function loadLines(pid: string) {
    const { data, error: err } = await supabase
      .from('budget_lines')
      .select('*')
      .eq('project_id', pid)
      .order('created_at', { ascending: false });
    if (err) toast.error(err.message);
    else setLines((data ?? []) as Line[]);
  }

  useEffect(() => {
    if (projectId) void loadLines(projectId);
  }, [projectId]);

  const totals = useMemo(() => {
    let income = 0;
    let expense = 0;
    for (const l of lines) {
      if (l.kind === 'income') income += Number(l.amount);
      else expense += Number(l.amount);
    }
    return { income, expense, balance: income - expense };
  }, [lines]);

  const monthly = useMemo(() => {
    type MonthAgg = { income: number; expense: number };
    const map = new Map<string, MonthAgg>();

    for (const l of lines) {
      const dt = l.line_date ? new Date(l.line_date) : null;
      if (!dt || Number.isNaN(dt.getTime())) continue;
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const prev = map.get(key) ?? { income: 0, expense: 0 };
      if (l.kind === 'income') prev.income += Number(l.amount);
      else prev.expense += Number(l.amount);
      map.set(key, prev);
    }

    const keys = Array.from(map.keys()).sort();
    return keys.map((key) => {
      const [y, m] = key.split('-').map((x) => Number(x));
      const dt = new Date(y, m - 1, 1);
      const agg = map.get(key)!;
      return { key, label: dt.toLocaleString('fr-FR', { month: 'short' }), income: agg.income, expense: agg.expense };
    });
  }, [lines]);

  const chart = useMemo(() => {
    const n = monthly.length;
    if (n === 0) return null;

    const width = 680;
    const height = 220;
    const pad = 34;

    const incomeMax = Math.max(...monthly.map((p) => p.income), 0);
    const expenseMax = Math.max(...monthly.map((p) => p.expense), 0);
    const maxY = Math.max(incomeMax, expenseMax, 1);

    const getX = (i: number) => (n === 1 ? width / 2 : pad + (i * (width - pad * 2)) / (n - 1));
    const getY = (v: number) => height - pad - (v / maxY) * (height - pad * 2);

    const incomePts = monthly.map((p, i) => `${getX(i)},${getY(p.income)}`).join(' ');
    const expensePts = monthly.map((p, i) => `${getX(i)},${getY(p.expense)}`).join(' ');

    return { width, height, pad, maxY, incomePts, expensePts, getX, getY };
  }, [monthly]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!profile || !projectId) return;
    const { error: err } = await supabase.from('budget_lines').insert({
      user_id: profile.id,
      project_id: projectId,
      ...form,
    });
    if (err) toast.error(err.message);
    else {
      setForm({ category: BUDGET_CATEGORIES[0], label: '', amount: 0, kind: 'expense', notes: '', line_date: todayIso });
      toast.success('Ligne budgétaire enregistrée.', 'Budget');
      await loadLines(projectId);
    }
  }

  function requestDelete(line: Line) {
    setConfirmDelete({ open: true, lineId: line.id, lineLabel: line.label });
  }

  async function confirmDeleteLine() {
    if (!confirmDelete.lineId) return;
    if (!projectId) return;

    setDeletingLine(true);
    const { error: err } = await supabase.from('budget_lines').delete().eq('id', confirmDelete.lineId);
    setDeletingLine(false);

    if (err) {
      toast.error(err.message);
      return;
    }

    setConfirmDelete({ open: false, lineId: null, lineLabel: '' });
    toast.success('Ligne budgétaire supprimée.', 'Budget');
    await loadLines(projectId);
  }

  function closeConfirm() {
    if (deletingLine) return;
    setConfirmDelete({ open: false, lineId: null, lineLabel: '' });
  }

  return (
    <div className="u-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">Recettes, dépenses et solde par projet.</p>
        </div>
        <div className="u-actions">
          <div className="u-filters">
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
          <Link to="/app" className="btn btn-ghost">
            ← Retour
          </Link>
        </div>
      </div>

      <div className="u-kpi-grid u-kpi-grid-3">
        <article className="u-kpi">
          <p className="u-kpi-label">Recettes</p>
          <p className="u-kpi-value" style={{ fontSize: '1.35rem' }}>
            {totals.income.toLocaleString('fr-FR')}
          </p>
        </article>
        <article className="u-kpi">
          <p className="u-kpi-label">Dépenses</p>
          <p className="u-kpi-value" style={{ fontSize: '1.35rem' }}>
            {totals.expense.toLocaleString('fr-FR')}
          </p>
        </article>
        <article className="u-kpi">
          <p className="u-kpi-label">Solde</p>
          <p className="u-kpi-value" style={{ fontSize: '1.35rem' }}>
            {totals.balance.toLocaleString('fr-FR')}
          </p>
        </article>
      </div>

      <section className="u-card">
        <h3 className="u-section-title" style={{ marginTop: 0 }}>
          Évolution mensuelle (recettes vs dépenses)
        </h3>
        {chart ? (
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <svg
              width="100%"
              viewBox={`0 0 ${chart.width} ${chart.height}`}
              role="img"
              aria-label="Graphique évolution budget (recettes) et dépenses par mois"
            >
              {/* Axes horizontaux (4 repères) */}
              {[0.25, 0.5, 0.75, 1].map((t) => {
                const y = chart.height - chart.pad - t * (chart.height - chart.pad * 2);
                return (
                  <line
                    key={t}
                    x1={chart.pad}
                    y1={y}
                    x2={chart.width - chart.pad}
                    y2={y}
                    stroke="rgba(26,60,94,0.12)"
                  />
                );
              })}

              <polyline points={chart.incomePts} fill="none" stroke="rgba(27, 107, 69, 1)" strokeWidth="3" />
              <polyline points={chart.expensePts} fill="none" stroke="rgba(155, 44, 44, 1)" strokeWidth="3" />

              {monthly.map((p, i) => (
                <g key={p.key}>
                  <circle cx={chart.getX(i)} cy={chart.getY(Math.max(p.income, p.expense))} r="4" fill="rgba(26,60,94,0.25)" />
                  <text x={chart.getX(i)} y={chart.height - 12} textAnchor="middle" fontSize="12" fill="rgba(19,41,67,0.7)">
                    {p.label}
                  </text>
                </g>
              ))}
            </svg>

            <div>
              <div className="muted" style={{ marginBottom: '0.45rem' }}>
                Légende
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <span className="chip" style={{ background: 'rgba(27, 107, 69, 0.12)', color: 'var(--ok)' }}>
                  Recettes
                </span>
                <span className="chip" style={{ background: 'rgba(155, 44, 44, 0.12)', color: '#9b2c2c' }}>
                  Dépenses
                </span>
              </div>
            </div>
          </div>
        ) : (
          <p className="u-empty">Ajoutez des lignes budgétaires avec une date pour afficher le graphique.</p>
        )}
      </section>

      <section className="u-card">
        <h3 className="u-section-title" style={{ marginTop: 0 }}>
          Ajouter une ligne
        </h3>
        <form onSubmit={onSubmit}>
          <div className="grid-2">
            <div className="field">
              <label>Libellé</label>
              <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </div>
            <div className="field">
              <label>Montant</label>
              <input
                type="number"
                min={0}
                required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
              />
            </div>
            <div className="field">
              <label>Catégorie</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {BUDGET_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Type</label>
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                <option value="expense">Dépense</option>
                <option value="income">Recette</option>
              </select>
            </div>
            <div className="field">
              <label>Date</label>
              <input
                type="date"
                required
                value={form.line_date}
                onChange={(e) => setForm({ ...form, line_date: e.target.value })}
              />
            </div>
          </div>
          <div className="field">
            <label>Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <button className="btn btn-primary" type="submit" disabled={!projectId}>
            Enregistrer la ligne
          </button>
        </form>
      </section>

      <section className="u-card">
        <h3 className="u-section-title" style={{ marginTop: 0 }}>
          Lignes
        </h3>
        {lines.length === 0 ? <p className="u-empty">Aucune ligne budgétaire.</p> : null}
        <table className="data-table">
          <thead>
            <tr>
              <th align="left">Date</th>
              <th align="left">Libellé</th>
              <th align="left">Catégorie</th>
              <th align="left">Type</th>
              <th align="right">Montant</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id}>
                <td>{l.line_date ? new Date(l.line_date).toLocaleDateString('fr-FR') : '—'}</td>
                <td>{l.label}</td>
                <td>{l.category}</td>
                <td>{l.kind === 'income' ? 'Recette' : 'Dépense'}</td>
                <td align="right">{Number(l.amount).toLocaleString('fr-FR')}</td>
                <td>
                  <button type="button" className="btn btn-ghost" onClick={() => requestDelete(l)}>
                    Suppr.
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {confirmDelete.open ? (
        <div className="budget-confirm-backdrop" role="presentation" onClick={closeConfirm}>
          <div
            className="budget-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="budget-delete-title"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="budget-confirm-head">
              <div>
                <h3 id="budget-delete-title" className="budget-confirm-title">
                  Supprimer cette ligne budgétaire ?
                </h3>
                <p className="budget-confirm-text">
                  {confirmDelete.lineLabel
                    ? `« ${confirmDelete.lineLabel} » sera supprimée définitivement.`
                    : 'Cette ligne sera supprimée définitivement.'}
                </p>
              </div>
              <button type="button" className="budget-confirm-close" onClick={closeConfirm} aria-label="Fermer">
                ×
              </button>
            </div>

            <div className="budget-confirm-actions">
              <button type="button" className="btn btn-ghost" onClick={closeConfirm} disabled={deletingLine}>
                Annuler
              </button>
              <button
                type="button"
                className="btn budget-btn-danger"
                onClick={() => void confirmDeleteLine()}
                disabled={deletingLine}
              >
                {deletingLine ? 'Suppression…' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
